import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface PdfViewerProps {
    component: any;
    value: any;
    data: any;
    row: any;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ component, value, data }) => {
    const [pdfSrc, setPdfSrc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { pdfViewerDataFrom, fileDataKey, dmsDataKey, url, customStyle } = component;

    useEffect(() => {
        const fetchPdf = async () => {
            try {
                if (!pdfViewerDataFrom) return;

                if (pdfViewerDataFrom === 'base64' && value) {
                    // Logic adapted from Angular: value[fileDataKey][0].url
                    // Or if value itself is the base64 string
                    // Assuming value is the form data object for this key?
                    // Actually, in Form.io custom components, 'value' is usually the data for *this* component key.
                    // But the Angular code accessed `value[fileDataKey]`. This suggests 'value' might be the whole submission data?
                    // Form.io React wrapper usually passes 'value' as the component's value.
                    // If the component relies on OTHER data (like fileDataKey), we should check props.data (submission data).

                    let targetValue = value;
                    if (fileDataKey && data && data[fileDataKey]) {
                        targetValue = data[fileDataKey];
                    }

                    if (targetValue && Array.isArray(targetValue) && targetValue.length > 0 && targetValue[0].url) {
                        setPdfSrc(targetValue[0].url);
                    } else if (typeof targetValue === 'string' && targetValue.startsWith('data:')) {
                        setPdfSrc(targetValue);
                    }
                } else if (pdfViewerDataFrom === 'dms') {
                    // Fetch from DMS
                    // Endpoint: /dev/storage/api/download/file
                    // Body: value (or value[dmsDataKey])
                    let body = value;
                    if (dmsDataKey && value && value[dmsDataKey]) {
                        body = value[dmsDataKey];
                    }

                    // If body is empty, we can't fetch
                    if (!body) return;

                    const response = await axios.post('/designer/storage/api/download/file', body, { // Proxy path adjust?
                        responseType: 'blob',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token') || ''}` // TODO: fix token retrieval
                        }
                    });

                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const objectUrl = URL.createObjectURL(blob);
                    setPdfSrc(objectUrl);

                } else if (pdfViewerDataFrom === 'url') {
                    if (url) {
                        setPdfSrc(url);
                    }
                }
            } catch (err) {
                console.error("Error loading PDF", err);
                setError("Failed to load PDF");
            }
        };

        fetchPdf();
    }, [value, data, pdfViewerDataFrom, fileDataKey, dmsDataKey, url]);

    if (error) {
        return <div className="text-red-500 p-4 border border-red-300 rounded bg-red-50">{error}</div>;
    }

    if (!pdfSrc) {
        // Placeholder or empty state
        return <div className="p-4 border border-dashed border-gray-300 rounded text-gray-400 text-center">PDF Viewer (No Content)</div>;
    }

    return (
        <div style={{ width: '100%', height: '500px', ...parseStyle(customStyle) }}>
            <iframe
                src={pdfSrc}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="PDF Viewer"
            />
        </div>
    );
};

// Helper for custom style string
const parseStyle = (styleStr: string) => {
    const style: any = {};
    if (!styleStr) return style;
    styleStr.split(';').forEach(rule => {
        const [key, val] = rule.split(':');
        if (key && val) {
            style[key.trim()] = val.trim();
        }
    });
    return style;
}

export default PdfViewer;
